/**
 * HOOK DE CONEXIÓN - Monitorea estado de red en tiempo real
 * 
 * Uso:
 * const { isOnline, connectionType, showOfflineMessage } = useConnection();
 */

import { useState, useEffect, useCallback } from 'react';

export const useConnection = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState(
    navigator.connection?.effectiveType || 'unknown'
  );
  const [wasOffline, setWasOffline] = useState(false);

  const updateConnectionStatus = useCallback(() => {
    const online = navigator.onLine;
    
    if (!online && isOnline) {
      // Acabamos de perder conexión
      setWasOffline(true);
    } else if (online && !isOnline) {
      // Acabamos de recuperar conexión
      // Mantener wasOffline true para mostrar mensaje de "reconectado"
    }
    
    setIsOnline(online);
    
    if (navigator.connection) {
      setConnectionType(navigator.connection.effectiveType);
    }
  }, [isOnline]);

  useEffect(() => {
    window.addEventListener('online', updateConnectionStatus);
    window.addEventListener('offline', updateConnectionStatus);
    
    // También escuchar cambios en la calidad de conexión
    if (navigator.connection) {
      navigator.connection.addEventListener('change', updateConnectionStatus);
    }

    return () => {
      window.removeEventListener('online', updateConnectionStatus);
      window.removeEventListener('offline', updateConnectionStatus);
      if (navigator.connection) {
        navigator.connection.removeEventListener('change', updateConnectionStatus);
      }
    };
  }, [updateConnectionStatus]);

  // Limpiar el mensaje de "reconectado" después de 3 segundos
  useEffect(() => {
    if (isOnline && wasOffline) {
      const timer = setTimeout(() => {
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  return {
    isOnline,
    connectionType,
    wasOffline,
    isSlowConnection: ['slow-2g', '2g'].includes(connectionType),
    showOfflineMessage: !isOnline,
    showReconnectedMessage: isOnline && wasOffline
  };
};

export default useConnection;
